import React from 'react';
import i18n from '@/i18n/config';
import { Button, Accordion, AccordionItem, Badge, Chip } from '@heroui/react';
import { RiFilterLine, RiFilter3Line } from '@remixicon/react';

import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { FilterScoreRange } from './FilterScoreRange';
import { FilterOwnerMultiSelect } from './FilterOwnerMultiSelect';
import { FilterStatusMultiSelect } from './FilterStatusMultiSelect';
import { FilterCompetitorsMultiSelect } from './FilterCompetitorsMultiSelect';
import { FilterShopMultiSelect } from './FilterShopMultiSelect';
import { FilterEcommerceMultiSelect } from './FilterEcommerceMultiSelect';

interface AdvancedFiltersPanelProps {
  // Status filter (multi-select now)
  statusValue: string;
  onStatusChange: (value: string) => void;

  // Score filter
  scoreValue: string;
  onScoreChange: (value: string) => void;

  // Owner filter
  ownerValue: string;
  onOwnerChange: (value: string) => void;

  // Boolean filters
  ecommerceValue: boolean | null;
  onEcommerceChange: (value: boolean | null) => void;

  // Competitors filter
  competitorsValue: string;
  onCompetitorsChange: (value: string) => void;

  // Shop filter
  shopValue: string;
  onShopChange: (value: string) => void;

  // Date filters
  collectedDateValue: { start: Date | null; end: Date | null };
  onCollectedDateChange: (value: { start: Date | null; end: Date | null }) => void;
  syncedDateValue: { start: Date | null; end: Date | null };
  onSyncedDateChange: (value: { start: Date | null; end: Date | null }) => void;
  createdDateValue: { start: Date | null; end: Date | null };
  onCreatedDateChange: (value: { start: Date | null; end: Date | null }) => void;
  updatedDateValue: { start: Date | null; end: Date | null };
  onUpdatedDateChange: (value: { start: Date | null; end: Date | null }) => void;

  // Active filters count
  activeFiltersCount: number;
}

export function AdvancedFiltersPanel({
  statusValue,
  onStatusChange,
  scoreValue,
  onScoreChange,
  ownerValue,
  onOwnerChange,
  ecommerceValue,
  onEcommerceChange,
  competitorsValue,
  onCompetitorsChange,
  shopValue,
  onShopChange,
  collectedDateValue,
  onCollectedDateChange,
  syncedDateValue,
  onSyncedDateChange,
  createdDateValue,
  onCreatedDateChange,
  updatedDateValue,
  onUpdatedDateChange,
  activeFiltersCount
}: AdvancedFiltersPanelProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <Button
        variant="flat"
        color="primary"
        size="sm"
        onPress={() => setIsOpen(!isOpen)}
        startContent={<RiFilter3Line className="h-4 w-4" />}
      >
        Advanced Filters
        {activeFiltersCount > 0 && (
          <Chip size="sm" color="danger" variant="flat" className="ml-2">
            {activeFiltersCount}
          </Chip>
        )}
      </Button>

      {isOpen && (
        <div className="border-default-200 bg-content1 mt-3 w-full rounded-lg border p-4 shadow-sm">
          <Accordion variant="splitted" defaultExpandedKeys={['basic']}>
            <AccordionItem key="basic" title={i18n.t('filters.basicFilters')} aria-label={i18n.t('filters.basicFilters')}>
              <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-3">
                <FilterStatusMultiSelect
                  label={i18n.t('filters.status')}
                  value={statusValue}
                  onChange={onStatusChange}
                  className="w-full"
                />
                <FilterScoreRange label={i18n.t('copy.score_range')} value={scoreValue} onChange={onScoreChange} className="w-full" />
                <FilterOwnerMultiSelect label={i18n.t('copy.owner')} value={ownerValue} onChange={onOwnerChange} className="w-full" />
              </div>
            </AccordionItem>

            <AccordionItem key="technical" title={i18n.t('copy.technical_filters')} aria-label={i18n.t('copy.technical_filters')}>
              <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-3">
                <FilterEcommerceMultiSelect
                  label={i18n.t('copy.e_commerce')}
                  value={ecommerceValue}
                  onChange={onEcommerceChange}
                  className="w-full"
                />
                <FilterCompetitorsMultiSelect
                  label={i18n.t('copy.competitors')}
                  value={competitorsValue}
                  onChange={onCompetitorsChange}
                  className="w-full"
                />
                <FilterShopMultiSelect label={i18n.t('copy.shop')} value={shopValue} onChange={onShopChange} className="w-full" />
              </div>
            </AccordionItem>

            <AccordionItem key="dates" title={i18n.t('copy.date_filters')} aria-label={i18n.t('copy.date_filters')}>
              <div className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2 xl:grid-cols-4">
                <DateRangeFilter label={i18n.t('copy.collected_date')} value={collectedDateValue} onChange={onCollectedDateChange} />
                <DateRangeFilter label={i18n.t('copy.synced_date')} value={syncedDateValue} onChange={onSyncedDateChange} />
                <DateRangeFilter label={i18n.t('filters.createdDate')} value={createdDateValue} onChange={onCreatedDateChange} />
                <DateRangeFilter label={i18n.t('copy.updated_date')} value={updatedDateValue} onChange={onUpdatedDateChange} />
              </div>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </>
  );
}

export default AdvancedFiltersPanel;
